import {
    type Dispatch,
    type MouseEvent,
    type SetStateAction,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { CheckoutCategorySubtype, ClickType } from '@jsmdg/tracking';
import {
    Breakpoint,
    Button,
    ButtonColor,
    ButtonSize,
    GiftWrappingIcon,
    InputNumber,
    PureButton,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { hasDiscountCodeCampaignBadge } from '../../../shared/hasDiscountCodeCampaignBadge';
import { getBookableLocationsCount, isExperience } from '../../../shared/product-safeguards';
import { VoucherQuantity } from '../../helper/voucherQuantity';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { useOverlayContext } from '../../hooks/useOverlayContext';
import { usePayPalExpressError } from '../../hooks/usePayPalExpressError';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { FeatureFlagVariant } from '../../types/enums/featureFlagVariant';
import { PurchaseType } from '../../types/enums/purchaseType';
import { SellOption } from '../../types/enums/sellOption';
import { type ProductDetailContext } from '../../types/types';
import { AvailabilitiesContainer } from '../AvailabilitiesContainer/AvailabilitiesContainer';
import { ClubDiscountBadge } from '../ClubDiscountBadge';
import { DiscountCodeCampaignBadge } from '../DiscountCodeCampaignBadge';
import { FriendsDiscountBadge } from '../FriendsDiscountBadge';
import { PayPalBuyboxButton } from '../PayPalBuyboxButton';
import { RedeemInfo } from '../RedeemInfo';
import { trackBuyVoucherClicked } from './voucherAndTicketSelector.tracking';
import { trackVoucherCountSelection } from './VoucherCountSelection.tracking';
import styles from './VoucherAndTicketSelector.module.scss';

const messages = defineMessages({
    quantityLabel: {
        defaultMessage: 'Anzahl der Gutscheine',
    },
    participants: {
        defaultMessage:
            'für {participantsCountLabel} {maxParticipants, plural, one {Person} other {Personen}}',
    },
    voucherTitle: {
        defaultMessage: 'Gutschein schenken',
    },
    voucherBenefitValidity: {
        defaultMessage: '{years} Jahre gültig & verlängerbar',
    },
    voucherBenefitDelivery: {
        defaultMessage: 'Sofort per E-Mail als PDF erhältlich',
    },
    voucherBenefitRedeemPrefix: {
        defaultMessage: 'Für alle Erlebnisse',
    },
    decreaseQuantity: {
        defaultMessage: 'Gutscheinanzahl verringern',
    },
    increaseQuantity: {
        defaultMessage: 'Gutscheinanzahl erhöhen',
    },
    addToCart: {
        defaultMessage: 'In den Warenkorb',
    },
});

type VoucherAndTicketSelectorProps = {
    readonly minParticipants: number;
    readonly buyboxProps: UseBuyboxReturn;
    readonly maxParticipants: number;
    readonly isCheckoutDisabled: boolean;
    readonly showPayPalButton?: boolean;
    readonly setTargetElement: Dispatch<SetStateAction<HTMLElement | null>>;
    readonly isClubDeal: boolean;
};

const VoucherAndTicketSelector = ({
    buyboxProps,
    isCheckoutDisabled,
    isClubDeal,
    maxParticipants,
    minParticipants,
    setTargetElement,
    showPayPalButton = false,
}: VoucherAndTicketSelectorProps): JSX.Element => {
    const { handleAddToCartClick, handlePayPalAddToCartError, handlePurchaseType } = buyboxProps;
    const intl = useIntl();
    const { featureFlagsVariation, tenantConfig } = useFragmentContext<ProductDetailContext>();
    const product = useProduct();
    const bookableLocationsCount = getBookableLocationsCount(product);
    const { price, setMultiplier } = usePrice();
    const { discountBadge, hasFriendsDiscount, originalPrice } = product;
    const hasCampaignBadge = hasDiscountCodeCampaignBadge(discountBadge, Boolean(originalPrice));
    const { wrapAddToCartWithOverlay } = useOverlayContext();
    const handlePayPalError = usePayPalExpressError();
    const isDesktop = useBreakpoint(Breakpoint.SM);
    const checkoutButtonRef = useRef<HTMLButtonElement>(null);
    const [checkoutButtonHeight, setCheckoutButtonHeight] = useState(0);

    const participantsCountLabel =
        minParticipants && maxParticipants
            ? minParticipants === maxParticipants
                ? `${minParticipants}`
                : `${minParticipants}-${maxParticipants}`
            : '';

    useLayoutEffect(() => {
        if (!showPayPalButton) {
            setCheckoutButtonHeight(0);
            return;
        }

        if (checkoutButtonRef.current) {
            setCheckoutButtonHeight(checkoutButtonRef.current.offsetHeight);
        }
    }, [showPayPalButton, isCheckoutDisabled, isDesktop]);

    const handleBuyboxButtonClick = (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
        categorySubtype?: CheckoutCategorySubtype,
    ): void | Promise<void> => {
        wrapAddToCartWithOverlay(handleAddToCartClick, {
            price,
            event,
            clickType,
            categorySubtype,
        });
    };

    const selectVoucher = (): void => {
        handlePurchaseType(PurchaseType.Voucher);
    };

    const handleVoucherSelection = (): void => {
        selectVoucher();
        trackBuyVoucherClicked();
    };

    const handleVoucherCheckoutClick = (categorySubtype?: CheckoutCategorySubtype): void => {
        handleBuyboxButtonClick(undefined, undefined, categorySubtype);
    };

    const updateVoucherQuantity = async (nextQuantity: number): Promise<void> => {
        setMultiplier(nextQuantity);
        void trackVoucherCountSelection(product.title, nextQuantity - price.multiplier);
    };

    const showFriendDiscount = hasFriendsDiscount && !hasCampaignBadge;

    const enableRadicalTest =
        featureFlagsVariation.pdp_next_radical_ui === FeatureFlagVariant.ON &&
        isExperience(product) &&
        product.sellOption === SellOption.TicketAndVoucher;

    const isTicketOnly = isExperience(product) && product.sellOption === SellOption.TicketOnly;

    return (
        <div
            className={classNames('position-relative', {
                'mt-3x': isClubDeal || hasCampaignBadge || showFriendDiscount,
            })}
        >
            {isClubDeal ? (
                <ClubDiscountBadge
                    className={classNames('position-absolute', styles.clubDiscountBadge)}
                />
            ) : hasCampaignBadge ? (
                <DiscountCodeCampaignBadge className={styles.friendsDiscountBadge} />
            ) : (
                showFriendDiscount && (
                    <FriendsDiscountBadge className={styles.friendsDiscountBadge} />
                )
            )}

            <section
                className={classNames(styles.voucherCard, {
                    'pt-3x': isClubDeal,
                })}
                data-testid="voucher-card"
            >
                <div className={styles.voucherHeader}>
                    <div className={styles.voucherHeading}>
                        <GiftWrappingIcon className={styles.voucherIcon} size={24} />
                        <PureButton
                            className={styles.selectionButton}
                            onClick={handleVoucherSelection}
                        >
                            <span className={styles.selectionButtonText}>
                                {intl.formatMessage(messages.voucherTitle)}
                            </span>
                        </PureButton>
                    </div>
                </div>

                <ul className={styles.benefitsList}>
                    <li>
                        {intl.formatMessage(messages.voucherBenefitValidity, {
                            years: tenantConfig.voucherExpirationLimitInYears,
                        })}
                    </li>
                    <li>{intl.formatMessage(messages.voucherBenefitDelivery)}</li>
                    <li data-testid="voucherLocationCount">
                        {intl.formatMessage(messages.voucherBenefitRedeemPrefix)}{' '}
                        <RedeemInfo isInline />
                    </li>
                </ul>

                <div className={styles.quantityRow}>
                    <div className="d-flex flex-column w-100">
                        <span className={styles.quantityLabel}>
                            {intl.formatMessage(messages.quantityLabel)}
                        </span>
                        {participantsCountLabel && (
                            <span className={styles.participants}>
                                (
                                {intl.formatMessage(messages.participants, {
                                    participantsCountLabel,
                                    maxParticipants,
                                })}
                                )
                            </span>
                        )}
                    </div>
                    <InputNumber
                        value={price.multiplier}
                        minValue={VoucherQuantity.Min}
                        maxValue={VoucherQuantity.Max}
                        onQuantityChange={(nextQuantity: number) =>
                            updateVoucherQuantity(nextQuantity)
                        }
                        a11yDecrementText={intl.formatMessage(messages.decreaseQuantity)}
                        a11yIncrementText={intl.formatMessage(messages.increaseQuantity)}
                        a11yValue={`${intl.formatMessage(messages.quantityLabel)}: ${price.multiplier}`}
                    />
                </div>

                <div
                    ref={setTargetElement as (el: HTMLElement | null) => void}
                    className={styles.sentinel}
                />

                <div
                    className={classNames(styles.voucherButtonRow, {
                        [styles.checkoutButtonsRow]: showPayPalButton,
                    })}
                >
                    <Button
                        ref={checkoutButtonRef}
                        size={isDesktop ? ButtonSize.Large : ButtonSize.Small}
                        data-testid="voucher-checkout-button"
                        onClick={() => {
                            handleVoucherCheckoutClick(
                                isClubDeal ? CheckoutCategorySubtype.CART_BUY_NOW : undefined,
                            );
                        }}
                        disabled={isCheckoutDisabled}
                        className={classNames({
                            'w-100': !showPayPalButton,
                        })}
                        color={ButtonColor.Brand}
                        a11yText={intl.formatMessage(messages.addToCart)}
                    >
                        {intl.formatMessage(messages.addToCart)}
                    </Button>

                    {showPayPalButton && (
                        <PayPalBuyboxButton
                            buttonHeight={checkoutButtonHeight}
                            clickType={ClickType.DefaultUpper}
                            productId={product.productId}
                            quantity={price.multiplier}
                            disableButton={isCheckoutDisabled}
                            onAddToCartError={handlePayPalAddToCartError}
                            handleError={handlePayPalError}
                        />
                    )}
                </div>
            </section>
            {!!bookableLocationsCount && (
                <AvailabilitiesContainer
                    isAddToCartDisabled={isCheckoutDisabled}
                    onAddToCartClick={categorySubtype => {
                        handleVoucherCheckoutClick(categorySubtype as CheckoutCategorySubtype);
                    }}
                    isTicketOnly={isTicketOnly}
                    enableRadicalTest={enableRadicalTest}
                />
            )}
        </div>
    );
};

export { VoucherAndTicketSelector };
